import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import { LOADING_STATUS } from '../../common/constants';
import {
  fetchAdditionalServiceRulesDataByDefaultRateId,
  fetchLastMinuteRuleByDefaultRateId,
  fetchMinimumLengthOfStayRuleByDefaultRateId,
  fetchPreparationRestrictionRuleByDefaultRateId,
  fetchPriceChangeRuleByDefaultRateId,
  fetchReleaseIntervalRuleByDefaultRateId,
  fetchShortStayRuleByDefaultRateId,
  updateDefaultRateAdditionalServiceRules,
  updateDefaultRateLastMinuteRule,
  updateDefaultRateMinimumLengthOfStayRule,
  updateDefaultRatePreparationRestrictionRule,
  updateDefaultRatePriceChangeRule,
  updateDefaultRateReleaseIntervalRule,
  updateDefaultRateShortStayRule,
} from '../../common/api/defaultRateRulesService';
import {
  AddonServiceRuleItemsData,
  LastMinuteRuleDetails,
  MinimumLengthOfStayRuleDetails,
  PreparationRestrictionRuleDetails,
  PriceChangeRuleDetails,
  ReleaseIntervalRuleDetails,
  ShortStayRuleDetails,
} from '../../common/types/rates.types';

export interface DefaultRateRulesState {
  minimumLengthOfStayRuleDetails: MinimumLengthOfStayRuleDetails;
  preparationRestrictionRuleDetails: PreparationRestrictionRuleDetails;
  shortStayRuleDetails: ShortStayRuleDetails;
  lastMinuteRuleDetails: LastMinuteRuleDetails;
  priceChangeRuleDetails: PriceChangeRuleDetails;
  releaseIntervalRuleDetails: ReleaseIntervalRuleDetails;
  additionalServiceRulesItemsData: AddonServiceRuleItemsData;
  minimumLengthOfStayRuleDetailsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  preparationRestrictionRuleDetailsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  shortStayRuleDetailsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  lastMinuteRuleDetailsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  priceChangeRuleDetailsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  releaseIntervalRuleDetailsStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
  additionalServiceRulesItemsDataStatus:
    | LOADING_STATUS.IDLE
    | LOADING_STATUS.LOADING
    | LOADING_STATUS.FAILED;
}

const initialState: DefaultRateRulesState = {
  minimumLengthOfStayRuleDetails: {
    defaultRateId: '',
    minimumLengthOfStay: null,
  },
  preparationRestrictionRuleDetails: {
    defaultRateId: '',
    preparationDays: null,
  },
  shortStayRuleDetails: {
    defaultRateId: '',
    durationRestriction: null,
    priceChange: {
      priceChangeType: 0,
      difference: 0,
      percent: 0,
    },
  },
  lastMinuteRuleDetails: {
    defaultRateId: '',
    daysUntilStart: null,
    priceChange: {
      priceChangeType: 0,
      difference: 0,
      percent: 0,
    },
  },
  priceChangeRuleDetails: {
    defaultRateId: '',
    priceChange: {
      priceChangeType: 0,
      difference: 0,
      percent: 0,
    },
  },
  releaseIntervalRuleDetails: {
    defaultRateId: '',
    daysUntilRelease: null,
    releaseLengthOfStayRule: true,
    releaseWeekdayMinimumStayRules: true,
    releaseWeekdayCheckInRules: true,
    releaseWeekdayCheckOutRules: true,
  },
  additionalServiceRulesItemsData: {
    defaultRateId: '',
    additionalServiceRules: [],
  },
  minimumLengthOfStayRuleDetailsStatus: LOADING_STATUS.IDLE,
  preparationRestrictionRuleDetailsStatus: LOADING_STATUS.IDLE,
  shortStayRuleDetailsStatus: LOADING_STATUS.IDLE,
  lastMinuteRuleDetailsStatus: LOADING_STATUS.IDLE,
  priceChangeRuleDetailsStatus: LOADING_STATUS.IDLE,
  releaseIntervalRuleDetailsStatus: LOADING_STATUS.IDLE,
  additionalServiceRulesItemsDataStatus: LOADING_STATUS.IDLE,
};

export const getDefaultRateMinimumLengthOfStayRuleAsync = createAsyncThunk(
  'defaultRateRules/getDefaultRateMinimumLengthOfStayRule',
  async (defaultRateId: string, { rejectWithValue }) => {
    try {
      const response =
        await fetchMinimumLengthOfStayRuleByDefaultRateId(defaultRateId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getDefaultRatePreparationRestrictionRuleAsync = createAsyncThunk(
  'defaultRateRules/getDefaultRatePreparationRestrictionRule',
  async (defaultRateId: string, { rejectWithValue }) => {
    try {
      const response =
        await fetchPreparationRestrictionRuleByDefaultRateId(defaultRateId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getDefaultRateShortStayRuleAsync = createAsyncThunk(
  'defaultRateRules/getDefaultRateShortStayRule',
  async (defaultRateId: string, { rejectWithValue }) => {
    try {
      const response = await fetchShortStayRuleByDefaultRateId(defaultRateId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getDefaultRateLastMinuteRuleAsync = createAsyncThunk(
  'defaultRateRules/getDefaultRateLastMinuteRule',
  async (defaultRateId: string, { rejectWithValue }) => {
    try {
      const response = await fetchLastMinuteRuleByDefaultRateId(defaultRateId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getDefaultRatePriceChangeRuleAsync = createAsyncThunk(
  'defaultRateRules/getDefaultRatePriceChangeRule',
  async (defaultRateId: string, { rejectWithValue }) => {
    try {
      const response = await fetchPriceChangeRuleByDefaultRateId(defaultRateId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getDefaultRateReleaseIntervalRuleAsync = createAsyncThunk(
  'defaultRateRules/getDefaultRateReleaseIntervalRule',
  async (defaultRateId: string, { rejectWithValue }) => {
    try {
      const response =
        await fetchReleaseIntervalRuleByDefaultRateId(defaultRateId);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const getDefaultRateAdditionalServiceRulesDataAsync = createAsyncThunk(
  'defaultRateRules/getDefaultRateAdditionalServiceRulesData',
  async (
    {
      defaultRateId,
      accommodationId,
    }: { defaultRateId: string; accommodationId: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await fetchAdditionalServiceRulesDataByDefaultRateId(
        defaultRateId,
        accommodationId,
      );
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateDefaultRateMinimumLengthOfStayRuleAsync = createAsyncThunk(
  'defaultRateRules/updateDefaultRateMinimumLengthOfStayRule',
  async (
    minimumLengthOfStayRuleData: MinimumLengthOfStayRuleDetails,
    { rejectWithValue },
  ) => {
    try {
      const response = await updateDefaultRateMinimumLengthOfStayRule(
        minimumLengthOfStayRuleData,
      );
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateDefaultRatePreparationRestrictionRuleAsync =
  createAsyncThunk(
    'defaultRateRules/updateDefaultRatePreparationRestrictionRule',
    async (
      preparationRestrictionRuleData: PreparationRestrictionRuleDetails,
      { rejectWithValue },
    ) => {
      try {
        const response = await updateDefaultRatePreparationRestrictionRule(
          preparationRestrictionRuleData,
        );
        return response.result;
      } catch (error) {
        return rejectWithValue(error.response.data);
      }
    },
  );

export const updateDefaultRateShortStayRuleAsync = createAsyncThunk(
  'defaultRateRules/updateDefaultRateShortStayRule',
  async (shortStayRuleData: ShortStayRuleDetails, { rejectWithValue }) => {
    try {
      const response = await updateDefaultRateShortStayRule(shortStayRuleData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateDefaultRateLastMinuteRuleAsync = createAsyncThunk(
  'defaultRateRules/updateDefaultRateLastMinuteRule',
  async (lastMinuteRuleData: LastMinuteRuleDetails, { rejectWithValue }) => {
    try {
      const response =
        await updateDefaultRateLastMinuteRule(lastMinuteRuleData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateDefaultRatePriceChangeRuleAsync = createAsyncThunk(
  'defaultRateRules/updateDefaultRatePriceChangeRule',
  async (priceChangeRuleData: PriceChangeRuleDetails, { rejectWithValue }) => {
    try {
      const response =
        await updateDefaultRatePriceChangeRule(priceChangeRuleData);
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateDefaultRateReleaseIntervalRuleAsync = createAsyncThunk(
  'defaultRateRules/updateDefaultRateReleaseIntervalRuleAsync',
  async (
    releaseIntervalRuleData: ReleaseIntervalRuleDetails,
    { rejectWithValue },
  ) => {
    try {
      const response = await updateDefaultRateReleaseIntervalRule(
        releaseIntervalRuleData,
      );
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const updateDefaultRateAdditionalServiceRulesAsync = createAsyncThunk(
  'defaultRateRules/updateDefaultRateAdditionalServiceRules',
  async (
    addonServiceRuleItemsData: AddonServiceRuleItemsData,
    { rejectWithValue },
  ) => {
    try {
      const response = await updateDefaultRateAdditionalServiceRules(
        addonServiceRuleItemsData,
      );
      return response.result;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

const defaultRateRulesSlice = createSlice({
  name: 'defaultRateRules',
  initialState,
  reducers: {
    updateAdditionalServiceField: (state, action) => {
      const { index, field, value } = action.payload;
      const keys = field.split('.');
      const existingRule =
        state.additionalServiceRulesItemsData.additionalServiceRules.find(
          rule => rule.additionalServiceId === index,
        );
      if (existingRule) {
        let current = existingRule;
        for (let i = 0; i < keys.length - 1; i += 1) {
          current = current[keys[i]];
          if (!current) return;
        }
        current[keys[keys.length - 1]] = value;
      }
    },
    addAdditionalServiceRule: (state, action) => {
      state.additionalServiceRulesItemsData.additionalServiceRules.push(
        action.payload,
      );
    },
    removeAdditionalServiceRule: (state, action) => {
      const id = action.payload;
      state.additionalServiceRulesItemsData.additionalServiceRules =
        state.additionalServiceRulesItemsData.additionalServiceRules.filter(
          rule => rule.additionalServiceId !== id,
        );
    },
  },
  extraReducers: builder => {
    builder
      .addCase(
        getDefaultRateMinimumLengthOfStayRuleAsync.pending,
        (state, action) => {
          state.minimumLengthOfStayRuleDetailsStatus = LOADING_STATUS.LOADING;
        },
      )
      .addCase(
        getDefaultRateMinimumLengthOfStayRuleAsync.fulfilled,
        (state, action) => {
          state.minimumLengthOfStayRuleDetails = action.payload;
          state.minimumLengthOfStayRuleDetailsStatus = LOADING_STATUS.IDLE;
        },
      )
      .addCase(
        getDefaultRateMinimumLengthOfStayRuleAsync.rejected,
        (state, action) => {
          state.minimumLengthOfStayRuleDetailsStatus = LOADING_STATUS.FAILED;
        },
      )
      .addCase(
        getDefaultRatePreparationRestrictionRuleAsync.pending,
        (state, action) => {
          state.preparationRestrictionRuleDetailsStatus =
            LOADING_STATUS.LOADING;
        },
      )
      .addCase(
        getDefaultRatePreparationRestrictionRuleAsync.fulfilled,
        (state, action) => {
          state.preparationRestrictionRuleDetails = action.payload;
          state.preparationRestrictionRuleDetailsStatus = LOADING_STATUS.IDLE;
        },
      )
      .addCase(
        getDefaultRatePreparationRestrictionRuleAsync.rejected,
        (state, action) => {
          state.preparationRestrictionRuleDetailsStatus = LOADING_STATUS.FAILED;
        },
      )
      .addCase(getDefaultRateShortStayRuleAsync.pending, (state, action) => {
        state.shortStayRuleDetailsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getDefaultRateShortStayRuleAsync.fulfilled, (state, action) => {
        state.shortStayRuleDetails = action.payload;
        state.shortStayRuleDetailsStatus = LOADING_STATUS.IDLE;
      })
      .addCase(getDefaultRateShortStayRuleAsync.rejected, (state, action) => {
        state.shortStayRuleDetailsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getDefaultRateLastMinuteRuleAsync.pending, (state, action) => {
        state.lastMinuteRuleDetailsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(getDefaultRateLastMinuteRuleAsync.fulfilled, (state, action) => {
        state.lastMinuteRuleDetails = action.payload;
        state.lastMinuteRuleDetailsStatus = LOADING_STATUS.IDLE;
      })
      .addCase(getDefaultRateLastMinuteRuleAsync.rejected, (state, action) => {
        state.lastMinuteRuleDetailsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(getDefaultRatePriceChangeRuleAsync.pending, (state, action) => {
        state.priceChangeRuleDetailsStatus = LOADING_STATUS.LOADING;
      })
      .addCase(
        getDefaultRatePriceChangeRuleAsync.fulfilled,
        (state, action) => {
          state.priceChangeRuleDetails = action.payload;
          state.priceChangeRuleDetailsStatus = LOADING_STATUS.IDLE;
        },
      )
      .addCase(getDefaultRatePriceChangeRuleAsync.rejected, (state, action) => {
        state.priceChangeRuleDetailsStatus = LOADING_STATUS.FAILED;
      })
      .addCase(
        getDefaultRateReleaseIntervalRuleAsync.pending,
        (state, action) => {
          state.releaseIntervalRuleDetailsStatus = LOADING_STATUS.LOADING;
        },
      )
      .addCase(
        getDefaultRateReleaseIntervalRuleAsync.fulfilled,
        (state, action) => {
          state.releaseIntervalRuleDetails = action.payload;
          state.releaseIntervalRuleDetailsStatus = LOADING_STATUS.IDLE;
        },
      )
      .addCase(
        getDefaultRateReleaseIntervalRuleAsync.rejected,
        (state, action) => {
          state.releaseIntervalRuleDetailsStatus = LOADING_STATUS.FAILED;
        },
      )
      .addCase(
        getDefaultRateAdditionalServiceRulesDataAsync.pending,
        (state, action) => {
          state.additionalServiceRulesItemsDataStatus = LOADING_STATUS.LOADING;
        },
      )
      .addCase(
        getDefaultRateAdditionalServiceRulesDataAsync.fulfilled,
        (state, action) => {
          state.additionalServiceRulesItemsData = action.payload;
          state.additionalServiceRulesItemsDataStatus = LOADING_STATUS.IDLE;
        },
      )
      .addCase(
        getDefaultRateAdditionalServiceRulesDataAsync.rejected,
        (state, action) => {
          state.additionalServiceRulesItemsDataStatus = LOADING_STATUS.FAILED;
        },
      );
  },
});

export const {
  updateAdditionalServiceField,
  addAdditionalServiceRule,
  removeAdditionalServiceRule,
} = defaultRateRulesSlice.actions;

export const selectMinimumLengthOfStayRuleDetails = (state: RootState) =>
  state.defaultRateRules.minimumLengthOfStayRuleDetails;
export const selectMinimumLengthOfStayRuleDetailsLoadingStatus = (
  state: RootState,
) => state.defaultRateRules.minimumLengthOfStayRuleDetailsStatus;
export const selectPreparationRestrictionRuleDetails = (state: RootState) =>
  state.defaultRateRules.preparationRestrictionRuleDetails;
export const selectPreparationRestrictionRuleDetailsLoadingStatus = (
  state: RootState,
) => state.defaultRateRules.preparationRestrictionRuleDetailsStatus;
export const selectShortStayRuleDetails = (state: RootState) =>
  state.defaultRateRules.shortStayRuleDetails;
export const selectShortStayRuleDetailsLoadingStatus = (state: RootState) =>
  state.defaultRateRules.shortStayRuleDetailsStatus;
export const selectLastMinuteRuleDetails = (state: RootState) =>
  state.defaultRateRules.lastMinuteRuleDetails;
export const selectLastMinuteRuleDetailsLoadingStatus = (state: RootState) =>
  state.defaultRateRules.lastMinuteRuleDetailsStatus;
export const selectPriceChangeRuleDetails = (state: RootState) =>
  state.defaultRateRules.priceChangeRuleDetails;
export const selectPriceChangeRuleDetailsLoadingStatus = (state: RootState) =>
  state.defaultRateRules.priceChangeRuleDetailsStatus;
export const selectReleaseIntervalRuleDetails = (state: RootState) =>
  state.defaultRateRules.releaseIntervalRuleDetails;
export const selectReleaseIntervalRuleDetailsLoadingStatus = (
  state: RootState,
) => state.defaultRateRules.releaseIntervalRuleDetailsStatus;
export const selectAdditionalServiceRulesItemsData = (state: RootState) =>
  state.defaultRateRules.additionalServiceRulesItemsData;
export const selectAdditionalServiceRulesLoadingStatus = (state: RootState) =>
  state.defaultRateRules.additionalServiceRulesItemsDataStatus;

export default defaultRateRulesSlice.reducer;
